import axios from 'axios';
import CryptoJS from 'crypto-js';

// Configuration
const API_KEY = 'YOUR_API_KEY';
const API_SECRET = 'DCX_BOOKING_SECRET_KEY';
const IS_PROD = process.env.VUE_APP_IS_PROD === 'true';
const API_BASE_URL = IS_PROD ? process.env.VUE_APP_BACKEND_URL_PROD : process.env.VUE_APP_BACKEND_URL_DEV;

// Rate limiting configuration
const RATE_LIMIT = {
  maxRequests: 50,
  perMilliseconds: 1000,
  requestQueue: [],
  lastRequestTime: 0
};

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000
});

// Prevent double prefixing if the URL already starts with API_BASE_URL
axiosInstance.interceptors.request.use(
  (config) => {
    if (API_BASE_URL && config.url && config.url.startsWith(API_BASE_URL + '/')) {
      config.url = config.url.substring(API_BASE_URL.length);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Custom rate limiting implementation
const rateLimit = async (config) => {
  const now = Date.now();
  const timeSinceLastRequest = now - RATE_LIMIT.lastRequestTime;

  if (timeSinceLastRequest < RATE_LIMIT.perMilliseconds) {
    const delay = RATE_LIMIT.perMilliseconds - timeSinceLastRequest;
    await new Promise(resolve => setTimeout(resolve, delay));
  }

  RATE_LIMIT.lastRequestTime = Date.now();
  return config;
};

// Signature generation
const generateSignature = (data, timestamp) => {
  const payload = JSON.stringify(data) + timestamp + API_SECRET;
  return CryptoJS.SHA256(payload).toString();
};

async function getCsrfToken() {
  try {
    console.log("Fetching CSRF token...");
    const response = await fetch(`${API_BASE_URL}/csrf-token`);
    if (!response.ok) {
      console.error("Failed to fetch CSRF token:", response.statusText);
      return null;
    }
    const data = await response.json();
    console.log("Got CSRF token:", data.token?.substring(0, 10) + "...");
    return data.token;
  } catch (error) {
    console.error("Error fetching CSRF token:", error);
    return null;
  }
}

// Request interceptor with rate limiting
axiosInstance.interceptors.request.use(
  async (config) => {
    // Apply rate limiting
    await rateLimit(config);

    const timestamp = Date.now().toString();
    const nonce = Math.random().toString(36).substring(7);


    if (config.method !== 'get') {
      const token = await getCsrfToken();
      if (token) {
        config.headers['X-CSRF-Token'] = token;
      }
    }

    // Add authentication headers
    config.headers['X-API-Key'] = API_KEY;
    config.headers['X-Timestamp'] = timestamp;
    config.headers['X-Request-Nonce'] = nonce;

    // Generate signature
    const dataToSign = config.data || config.params || {};
    config.headers['X-Signature'] = generateSignature(dataToSign, timestamp);

    // Tự động đính kèm JWT token nếu có và chưa được set thủ công
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken && !config.headers['Authorization'] && !config.headers['authorization']) {
      config.headers['Authorization'] = `Bearer ${adminToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function for retry logic
const retryRequest = (error, delay) => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(axiosInstance(error.config));
    }, delay);
  });
};

// Response interceptor with enhanced error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      // Exclude login and refresh endpoints from automatic token refreshing
      if (originalRequest.url?.includes('/admin/login') || originalRequest.url?.includes('/admin/refresh')) {
        return Promise.reject(error);
      }

      if (originalRequest._retry) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('refreshToken');
        sessionStorage.setItem('session_expired_toast', '1');
        if (window.ToastService) {
          window.ToastService.error('Phiên làm việc đã hết hạn, vui lòng đăng nhập lại');
        }
        window.location.href = '/login';
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Thử refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('Không có refresh token');
        }

        const response = await axiosInstance.post(`/admin/refresh`, {}, {
          headers: { Authorization: `Bearer ${refreshToken}` }
        });

        // Cập nhật tokens mới
        const { access_token, refresh_token } = response.data;
        localStorage.setItem('adminToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);

        // Thử lại request ban đầu với token mới
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);

        // Đăng xuất người dùng
        localStorage.removeItem('adminToken');
        localStorage.removeItem('refreshToken');
        sessionStorage.setItem('session_expired_toast', '1');

        // Thông báo cho người dùng
        if (window.ToastService) {
          window.ToastService.error('Phiên làm việc đã hết hạn, vui lòng đăng nhập lại');
        }

        // Chuyển hướng đến trang login
        window.location.href = '/login';

        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;